import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resendVerification } from "../../../Store/userSlice";
import logo from "../../../assets/images/logo.png";
import "./VerifyEmailSent.css";

const RESEND_COOLDOWN = 60;

const VerifyEmailSent = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [countdown, setCountdown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState("");

  const dispatch = useDispatch();

  const startCountdown = useCallback(() => {
    setCountdown(RESEND_COOLDOWN);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleResend = async () => {
    if (!email || countdown > 0 || resendLoading) return;

    setResendLoading(true);
    setResendError("");
    setResendSuccess(false);

    try {
      await dispatch(resendVerification(email));
      setResendSuccess(true);
      startCountdown();
    } catch {
      setResendError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setResendLoading(false);
    }
  };

  const maskedEmail = email
    ? email.replace(/^(.{2}).*(@.*)$/, (_, a, b) => a + "••••" + b)
    : "votre adresse e-mail";

  return (
    <div className="verify-sent-page">
      <div className="verify-sent-card">
        <NavLink to="/">
          <img src={logo} alt="Casa del Padel" className="verify-sent-logo" />
        </NavLink>

        <div className="verify-sent-icon-wrap">
          <svg className="verify-sent-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="12" fill="#EAF4FD" />
            <path d="M4 8l8 5 8-5" stroke="#1a5689" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="4" y="7" width="16" height="11" rx="2" stroke="#1a5689" strokeWidth="1.5" />
          </svg>
        </div>

        <h1 className="verify-sent-title">Vérifiez votre boîte mail</h1>
        <p className="verify-sent-subtitle">
          Nous avons envoyé un lien de confirmation à{" "}
          <strong className="verify-sent-email">{maskedEmail}</strong>
        </p>

        <div className="verify-sent-steps">
          <div className="verify-sent-step">
            <span className="verify-sent-step-num">1</span>
            <span>Ouvrez l'e-mail intitulé <strong>« Vérifiez votre adresse e-mail »</strong></span>
          </div>
          <div className="verify-sent-step">
            <span className="verify-sent-step-num">2</span>
            <span>Cliquez sur le bouton <strong>« Confirmer mon adresse e-mail »</strong></span>
          </div>
          <div className="verify-sent-step">
            <span className="verify-sent-step-num">3</span>
            <span>Vous serez connecté automatiquement et pourrez profiter de Casa del Padel !</span>
          </div>
        </div>

        <div className="verify-sent-spam-tip">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Si vous ne trouvez pas l'e-mail, pensez à vérifier vos <strong>spams</strong>.
        </div>

        <div className="verify-sent-resend-section">
          {resendSuccess && (
            <div className="verify-sent-resend-success">
              E-mail renvoyé avec succès !
            </div>
          )}
          {resendError && (
            <div className="verify-sent-resend-error">{resendError}</div>
          )}

          <p className="verify-sent-resend-label">Vous n'avez rien reçu ?</p>
          <button
            className="verify-sent-resend-btn"
            onClick={handleResend}
            disabled={countdown > 0 || resendLoading || !email}
          >
            {resendLoading
              ? "Envoi en cours…"
              : countdown > 0
              ? `Renvoyer dans ${countdown}s`
              : "Renvoyer l'e-mail"}
          </button>
        </div>

        <NavLink to="/login" className="verify-sent-login-link">
          Retour à la connexion
        </NavLink>
      </div>
    </div>
  );
};

export default VerifyEmailSent;
